import { useState, FormEvent, FC } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import TextEdit from '../../UI/TextEdit';
import Button from '../../UI/Button';

import styles from './Application.module.scss';
import {
  ActiveApplicationDto,
  ApplicationRevenues,
  ApplicationSubmitRevenuesDto,
  FinancingPurpose,
  IsoCurrencyCode,
} from '../../../types/api';
import {
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps,
  styled,
} from '@mui/material';
import { COLORS } from '../../../constants/colors';

const SelectStyled = styled((props: SelectProps<string>) => (
  <Select {...props} />
))(() => ({
  '.MuiSelect-select': {
    color: COLORS.text,
    padding: '0.25rem 0 !important',
    fontWeight: 400,
    fontSize: '1rem',
    lineHeight: 'initial',
    fontFamily: '"Inter", sans-serif',
  },

  fieldset: {
    border: 'none',
    borderBottom: '1px solid #939393',
    borderRadius: '0px',
  },
  '.MuiSelect-icon': {
    right: '0',
  },
}));

const purposeOptions = new Map<FinancingPurpose, string>([
  [FinancingPurpose.RESTOCK, 'application-revenue.purpose-options.restock'],
  [FinancingPurpose.CASHFLOW, 'application-revenue.purpose-options.cashflow'],
  [FinancingPurpose.MARKETING, 'application-revenue.purpose-options.marketing'],
  [FinancingPurpose.BRANCH, 'application-revenue.purpose-options.branch'],
  [FinancingPurpose.EQUIPMENT, 'application-revenue.purpose-options.equipment'],
  [FinancingPurpose.OTHER, 'application-revenue.purpose-options.other'],
]);

interface IApplicationRevenueProps {
  handleRevenuePurposeSubmit: (data: ApplicationSubmitRevenuesDto) => void;
  revenues: ApplicationRevenues;
  purpose: FinancingPurpose;
  applicationData: ActiveApplicationDto;
}

const ApplicationRevenue: FC<IApplicationRevenueProps> = ({
  handleRevenuePurposeSubmit,
  revenues,
  purpose,
  applicationData,
}) => {
  const { t } = useTranslation();

  const currency = applicationData.product?.currency as IsoCurrencyCode;

  const [formRevenue, setFormRevenue] = useState<ApplicationRevenues>({
    repaymentsMonthAvg: revenues?.repaymentsMonthAvg,
    revenuesYear: revenues?.revenuesYear,
    expensesYear: revenues?.expensesYear,
  });
  const [formPurpose, setFormPurpose] = useState<FinancingPurpose>(purpose);

  const submitHandler = (event: FormEvent) => {
    event.preventDefault();
    handleRevenuePurposeSubmit({ revenues: formRevenue, purpose: formPurpose });
  };

  const handleInputChange = (
    field: keyof ApplicationRevenues,
    value: string
  ) => {
    setFormRevenue((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div>
      <div className='gradient info'>
        <h1>
          <span>{t('application-revenue.title')}</span>
        </h1>
      </div>

      <div className={styles['body']}>
        <p>
          <Trans
            i18nKey='application-revenue.txt'
            components={[<strong></strong>]}
          ></Trans>
        </p>

        <form onSubmit={submitHandler}>
          <div
            className={styles['form-wrapper']}
            style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
          >
            <div>
              <InputLabel
                id='purpose-select-label'
                shrink
                sx={{
                  lineHeight: 'initial',
                }}
              >
                {t('application-revenue.purpose')}
              </InputLabel>
              <SelectStyled
                labelId='purpose-select-label'
                id='purpose-select'
                label={t('application-revenue.purpose')}
                value={formPurpose}
                onChange={(event: SelectChangeEvent<string>) => {
                  setFormPurpose(event.target.value as FinancingPurpose);
                }}
                variant='outlined'
                placeholder={t('application-revenue.purpose')}
                required
                fullWidth
              >
                {Array.from(purposeOptions.entries()).map(([key, value]) => (
                  <MenuItem key={key} value={key}>
                    {t(value)}
                  </MenuItem>
                ))}
              </SelectStyled>
            </div>
            <TextEdit
              id='revenuesYear'
              label={t('application-revenue.revenues-year')}
              value={String(formRevenue.revenuesYear)}
              type='number'
              required
              currency={currency}
              onChange={(value: string) => {
                handleInputChange('revenuesYear', value);
              }}
              fullWidth
            />
            <TextEdit
              id='expensesYear'
              label={t('application-revenue.expenses-year')}
              value={String(formRevenue.expensesYear)}
              type='number'
              required
              currency={currency}
              onChange={(value: string) => {
                handleInputChange('expensesYear', value);
              }}
              fullWidth
            />
            <TextEdit
              id='repaymentsMonthAvg'
              label={t('application-revenue.repayments-month-avg')}
              value={String(formRevenue.repaymentsMonthAvg)}
              type='number'
              required
              currency={currency}
              onChange={(value: string) => {
                handleInputChange('repaymentsMonthAvg', value);
              }}
              fullWidth
            />
          </div>

          <div className={styles['form-wrapper']}>
            <div className={styles.button}>
              <Button
                type='submit'
                className='application'
                id='app_declared_income'
              >
                {t('application-button-continue')}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApplicationRevenue;
